/**
 * Location Detail hook using React Relay
 *
 * This hook fetches detailed location information including nested relationships,
 * hardware at location, rates, and statistics for the location detail page.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useLocationDetailRelayQuery as UseLocationDetailRelayQuery } from './__generated__/useLocationDetailRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for single location with all detail fields
 */
const LocationDetailQuery = graphql`
  query useLocationDetailRelayQuery($id: ID!) {
    getLocation(id: $id) {
      # Core fields
      id
      oldId
      name
      state
      configuration
      createdOn

      # Geographic
      lat
      lng
      polygonCoordinates
      radius
      zoneType
      slowZoneSpeed

      # Capacity & Counts
      capacity
      availableHardwareCount
      totalHardwareCount
      totalRentableHardwareCount

      # Reservations & Rentals
      reservations
      maxReservations
      preventEndingRentals
      preventStartingRentals

      # Statistics
      rentalsStartedCount
      rentalsEndedCount
      avgRentalsStartedPerUser
      avgRentalsEndedPerUser
      uniqueUsersStartedCount
      uniqueUsersEndedCount

      # Access & Other
      private
      module
      operatingHours
      bannerId

      # Organization & System
      organizationId
      systemId
      systemOldId

      # System relationship
      system {
        id
        oldId
        name
        timezone
        # Time Restrictions flag + the window used when the station has no hours
        operatingHours
        openTimeInt
        closeTimeInt
      }

      # Icon/Image
      iconId
      icon {
        id
        oldId
        name
        type
        approvedOn
        comment
      }

      # Rates
      rates {
        id
        oldId
        name
        baseRate
        baseRateIncludedTime
        freeTime
        pricePerPeriod
        periodDuration
        maxPeriods
        maxRentals
        creditCardHold
        minimumWalletDeposit
        membershipRequired
        hidden
        activeOn
        archivedOn
        createdOn
      }

      # Hardware at this location
      hardware(limit: 100, sort: [{ field: NUMBER, order: ASC }]) {
        id
        oldId
        number
        label
        mac
        imei
        state
        isAvailable
        isActive
        isOnline
        lastSeen
        batteryPercent
        batteryVoltage
        vendorId
        vendorSerial
        hardwareType {
          id
          name
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * System type from GraphQL (nested in Location)
 */
export interface LocationSystemDetailGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  timezone?: string | null;
  /** Settings › Rentals › "Time Restrictions"; ant ignores all hours when off */
  operatingHours?: boolean | null;
  /** System open/close as seconds of the UTC day (used when a station has no hours) */
  openTimeInt?: number | null;
  closeTimeInt?: number | null;
}

/**
 * Icon/Image type from GraphQL
 */
export interface LocationIconDetailGraphQL {
  id: string;
  oldId?: string | null;
  name?: string | null;
  type?: string | null;
  approvedOn?: string | null;
  comment?: string | null;
}

/**
 * Rate type from GraphQL
 */
export interface LocationRateDetailGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  baseRate?: number | null;
  baseRateIncludedTime?: number | null;
  freeTime?: number | null;
  pricePerPeriod?: number | null;
  periodDuration?: number | null;
  maxPeriods?: number | null;
  maxRentals?: number | null;
  creditCardHold?: number | null;
  minimumWalletDeposit?: number | null;
  membershipRequired?: boolean | null;
  hidden?: boolean | null;
  activeOn?: string | null;
  archivedOn?: string | null;
  createdOn?: string | null;
}

/**
 * Hardware at location type from GraphQL
 */
export interface LocationHardwareGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  label?: string | null;
  mac?: string | null;
  imei?: string | null;
  state?: number | null;
  isAvailable?: boolean | null;
  isActive?: boolean | null;
  isOnline?: boolean | null;
  lastSeen?: string | null;
  batteryPercent?: number | null;
  batteryVoltage?: number | null;
  vendorId?: string | null;
  vendorSerial?: string | null;
  hardwareType?: {
    id: string;
    name?: string | null;
  } | null;
}

/**
 * Location detail type from GraphQL - includes all detail fields
 */
export interface LocationDetailGraphQL {
  // Core fields
  id: string;
  oldId?: string | null;
  name: string;
  state?: number | null;
  configuration?: string | null;
  createdOn: string;

  // Geographic
  lat?: number | null;
  lng?: number | null;
  polygonCoordinates?: ReadonlyArray<ReadonlyArray<number>> | null;
  radius?: number | null;
  zoneType?: string | null;
  slowZoneSpeed?: number | null;

  // Capacity & Counts
  capacity?: number | null;
  availableHardwareCount: number;
  totalHardwareCount: number;
  totalRentableHardwareCount: number;

  // Reservations & Rentals
  reservations?: boolean | null;
  maxReservations?: number | null;
  preventEndingRentals?: boolean | null;
  preventStartingRentals?: boolean | null;

  // Statistics
  rentalsStartedCount: number;
  rentalsEndedCount: number;
  avgRentalsStartedPerUser?: number | null;
  avgRentalsEndedPerUser?: number | null;
  uniqueUsersStartedCount: number;
  uniqueUsersEndedCount: number;

  // Access & Other
  private?: boolean | null;
  module?: string | null;
  /**
   * Json: { v1: { "0": [{ open, closed }], ... } } by UTC day, seconds of the UTC
   * day; null = the system's hours apply. See util/operatingHours.ts.
   */
  operatingHours?: unknown;
  bannerId?: string | null;

  // Organization & System
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;

  // System relationship
  system?: LocationSystemDetailGraphQL | null;

  // Icon/Image
  iconId?: string | null;
  icon?: LocationIconDetailGraphQL | null;

  // Rates
  rates?: LocationRateDetailGraphQL[] | null;

  // Hardware at this location
  hardware?: LocationHardwareGraphQL[] | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching location detail using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function LocationDetail({ id }: { id: string }) {
 *   return (
 *     <Suspense fallback={<LocationDetailSkeleton />}>
 *       <LocationDetailContent id={id} />
 *     </Suspense>
 *   );
 * }
 *
 * function LocationDetailContent({ id }: { id: string }) {
 *   const location = useLocationDetailRelay(id);
 *   return <div>{location?.name}</div>;
 * }
 */
export function useLocationDetailRelay(locationId: string) {
  const data = useLazyLoadQuery<UseLocationDetailRelayQuery>(LocationDetailQuery, {
    id: locationId,
  });

  return data.getLocation as LocationDetailGraphQL | null;
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for location detail
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched } = useLocationDetailRelayQuery(locationId);
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.name}</div>;
 */
export function useLocationDetailRelayQuery(
  locationId: string | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<LocationDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(locationId);
  const refetchInterval = options?.refetchInterval;

  // Use refs to avoid recreating doFetch on every render
  const isFetchedRef = useRef(isFetched);
  const onErrorRef = useRef(options?.onError);

  // Keep refs in sync
  useEffect(() => {
    isFetchedRef.current = isFetched;
  }, [isFetched]);

  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  // Memoize the fetch function with stable dependencies
  const doFetch = useCallback(async () => {
    if (!locationId) return;

    try {
      if (isFetchedRef.current) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }

      const result = await fetchQuery<UseLocationDetailRelayQuery>(
        relayEnvironment,
        LocationDetailQuery,
        { id: locationId }
      ).toPromise();

      if (result) {
        setData(result.getLocation as LocationDetailGraphQL | null);
        setError(null);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch location');
      setError(errorObj);
      onErrorRef.current?.(errorObj);
    } finally {
      setIsLoading(false);
      setIsFetched(true);
      setIsRefetching(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    doFetch();
  }, [enabled, locationId, fetchCount, doFetch]);

  // Set up refetch interval
  useEffect(() => {
    if (!enabled || !refetchInterval || refetchInterval <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      doFetch();
    }, refetchInterval);

    return () => clearInterval(intervalId);
  }, [enabled, refetchInterval, doFetch]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      isRefetching,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, isRefetching, error, refetch]
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get available hardware count at location
 */
export function getAvailableHardwareCount(
  location: LocationDetailGraphQL | null
): number {
  if (!location) return 0;
  return location.availableHardwareCount;
}

/**
 * Get total hardware count at location
 */
export function getTotalHardwareCount(location: LocationDetailGraphQL | null): number {
  if (!location) return 0;
  return location.totalHardwareCount;
}

/**
 * Get location capacity utilization percentage
 */
export function getCapacityUtilization(location: LocationDetailGraphQL | null): number {
  if (!location || !location.capacity || location.capacity === 0) return 0;
  return Math.round((location.totalHardwareCount / location.capacity) * 100);
}

/**
 * Check if location has reservations enabled
 */
export function hasReservationsEnabled(location: LocationDetailGraphQL | null): boolean {
  if (!location) return false;
  return location.reservations ?? false;
}

/**
 * Get max reservations for location
 */
export function getMaxReservations(location: LocationDetailGraphQL | null): number {
  if (!location) return 0;
  return location.maxReservations ?? 0;
}

/**
 * Check if location is a zone (has polygon coordinates)
 */
export function isZoneLocation(location: LocationDetailGraphQL | null): boolean {
  if (!location) return false;
  return (
    location.configuration?.toUpperCase() === 'ZONE' ||
    (location.polygonCoordinates !== null &&
      location.polygonCoordinates !== undefined &&
      location.polygonCoordinates.length > 0)
  );
}

/**
 * Get active (non-archived) rates for location
 */
export function getActiveRates(
  location: LocationDetailGraphQL | null
): LocationRateDetailGraphQL[] {
  if (!location?.rates) return [];
  return location.rates.filter((rate) => !rate.archivedOn);
}

/**
 * Get available hardware at location
 */
export function getAvailableHardware(
  location: LocationDetailGraphQL | null
): LocationHardwareGraphQL[] {
  if (!location?.hardware) return [];
  return location.hardware.filter((hw) => hw.isAvailable);
}

/**
 * Get location statistics summary
 */
export function getLocationStats(location: LocationDetailGraphQL | null): {
  rentalsStarted: number;
  rentalsEnded: number;
  avgRentalsStartedPerUser: number;
  avgRentalsEndedPerUser: number;
  uniqueUsersStarted: number;
  uniqueUsersEnded: number;
} {
  if (!location) {
    return {
      rentalsStarted: 0,
      rentalsEnded: 0,
      avgRentalsStartedPerUser: 0,
      avgRentalsEndedPerUser: 0,
      uniqueUsersStarted: 0,
      uniqueUsersEnded: 0,
    };
  }
  return {
    rentalsStarted: location.rentalsStartedCount,
    rentalsEnded: location.rentalsEndedCount,
    avgRentalsStartedPerUser: location.avgRentalsStartedPerUser ?? 0,
    avgRentalsEndedPerUser: location.avgRentalsEndedPerUser ?? 0,
    uniqueUsersStarted: location.uniqueUsersStartedCount,
    uniqueUsersEnded: location.uniqueUsersEndedCount,
  };
}
